import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";
import { LoadingSpinner } from "components/LoadingSpinner";
import { PoweredByBadge } from "components/PoweredByBadge";
import { UserRole } from "constants/userRole";
import { useLoading } from "context/LoadingContext";
import { useUserWithAccount } from "context/UserAccountProvider";
import { ConfirmDialog } from "primereact/confirmdialog";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { authSelector, onTokenChange } from "store/slices/auth";
import { userSelector } from "store/slices/user";
import styled from "styled-components";

import { Footer } from "../Footer";
import { Loading } from "../Loading";
import { Navbar } from "../Navbar";
import { Sidebar } from "../Sidebar";

const ContentContainer = styled.div<{ $hideSidebar?: boolean }>`
  width: 100%;
  min-height: 100vh;

  /* The left side still has to clear the 96px fixed sidebar; the 0.825rem is the
     gap between it and the content. The bottom clears the fixed "Powered by"
     badge — its height plus the offset it floats at, plus a 14px gap. */
  padding: calc(var(--navbar-height) + 24px) 0.825rem
    calc(var(--powered-by-badge-height) + var(--powered-by-badge-offset) + 14px)
    ${({ $hideSidebar }) =>
      $hideSidebar ? "0.825rem" : "calc(96px + 0.825rem)"};

  background: var(--brand-gray-50);
`;

function AppLayoutElement() {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { token } = useSelector(authSelector);
  const { isLoading } = useLoading();

  const { accountRefetch } = useUserWithAccount();
  const { currentAccount, scope, name, email } = useSelector(userSelector);

  const initialLoad = async () => {
    let token: string;
    try {
      token = await getAccessTokenSilently();
    } catch (err: any) {
      console.log("error", err);
      loginWithRedirect();
      return;
    }

    dispatch(onTokenChange({ token }));

    setTimeout(() => {
      accountRefetch();
    }, 500);
  };

  useEffect(() => {
    initialLoad();
  }, []);

  useEffect(() => {
    if (scope && location.pathname === "/") {
      navigate(`/hospitals/view/${currentAccount}`);
    }
  }, [scope, location, currentAccount]);

  if (token && currentAccount) {
    const isHospitalStaff = scope === UserRole.HOSPITAL_STAFF;
    //
    return (
      <>
        <Navbar />
        {!isHospitalStaff && <Sidebar />}
        <ConfirmDialog />

        <ContentContainer $hideSidebar={isHospitalStaff}>
          {isLoading && <Loading />}

          <Outlet />
        </ContentContainer>

        {isHospitalStaff && <Footer />}

        <PoweredByBadge />
      </>
    );
  }

  return (
    <ContentContainer>
      <LoadingSpinner />
    </ContentContainer>
  );
}

export const AppLayout = withAuthenticationRequired(AppLayoutElement, {});
